(() => {
if (window.__ygNewsArticleScriptLoaded) return;
window.__ygNewsArticleScriptLoaded = true;

class YGArticleToc extends HTMLElement {
  connectedCallback() {
    this._sectionRoot = this.closest('.shopify-section') || document;
    this._prose = this._sectionRoot.querySelector('.yg-news-article-content');
    this._list = this.querySelector('.yg-article-toc__list');
    this._nav = this.querySelector('.yg-article-toc__nav');

    if (!this._prose || !this._list) return;

    this._buildGroups();
    this._links = Array.from(this.querySelectorAll('[data-toc-link]'));
    this._headings = this._links
      .map(function (link) { return document.getElementById(link.getAttribute('data-toc-link')); })
      .filter(Boolean);

    this._onClick = this._handleClick.bind(this);
    this._onUserScrollIntent = function () { this._manualSelection = false; }.bind(this);
    this.addEventListener('click', this._onClick);
    window.addEventListener('wheel', this._onUserScrollIntent, { passive: true });
    window.addEventListener('touchstart', this._onUserScrollIntent, { passive: true });
    window.addEventListener('keydown', this._onUserScrollIntent);

    this._observer = new IntersectionObserver(function (entries) {
      if (this._manualSelection) return;
      const visible = entries.filter(function (entry) { return entry.isIntersecting; });
      if (!visible.length) return;
      const activeEntry = visible.reduce(function (nearest, entry) {
        return Math.abs(entry.boundingClientRect.top) < Math.abs(nearest.boundingClientRect.top) ? entry : nearest;
      });
      this._setActive(activeEntry.target.id);
    }.bind(this), {
      root: null,
      threshold: 0.01,
      rootMargin: '-96px 0px -72% 0px',
    });

    this._headings.forEach(function (heading) { this._observer.observe(heading); }.bind(this));

    const hashId = this._readHash();
    if (hashId && this._links.some(function (link) { return link.getAttribute('data-toc-link') === hashId; })) {
      this._setActive(hashId);
    } else if (this._links[0]) {
      this._setActive(this._links[0].getAttribute('data-toc-link'));
    }
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
    if (this._onClick) this.removeEventListener('click', this._onClick);
    if (this._onUserScrollIntent) {
      window.removeEventListener('wheel', this._onUserScrollIntent);
      window.removeEventListener('touchstart', this._onUserScrollIntent);
      window.removeEventListener('keydown', this._onUserScrollIntent);
    }
  }

  _buildGroups() {
    const items = Array.from(this._list.children);
    let currentParent = null;
    let currentSublist = null;

    items.forEach(function (item) {
      if (item.dataset.tocLevel === 'h2') {
        currentParent = item;
        currentSublist = null;
        return;
      }

      if (!currentParent) return;

      if (!currentSublist) {
        const toggle = currentParent.querySelector('.yg-article-toc__toggle');
        currentSublist = document.createElement('ol');
        currentSublist.className = 'yg-article-toc__sublist';
        currentSublist.setAttribute('role', 'list');
        currentSublist.id = toggle.getAttribute('aria-controls');
        currentSublist.hidden = true;
        currentParent.appendChild(currentSublist);
        toggle.hidden = false;
      }

      currentSublist.appendChild(item);
    });

    const firstToggle = this.querySelector('.yg-article-toc__toggle:not([hidden])');
    if (firstToggle) this._setExpanded(firstToggle, true);
  }

  _handleClick(event) {
    const toggle = event.target.closest('.yg-article-toc__toggle');
    if (toggle) {
      this._setExpanded(toggle, toggle.getAttribute('aria-expanded') !== 'true');
      return;
    }

    const link = event.target.closest('[data-toc-link]');
    if (!link) return;

    this._manualSelection = true;
    this._setActive(link.getAttribute('data-toc-link'));
  }

  _setExpanded(toggle, expanded) {
    const sublist = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!sublist) return;
    toggle.setAttribute('aria-expanded', String(expanded));
    sublist.hidden = !expanded;
  }

  _setActive(id) {
    if (!id) return;

    let activeLink = null;
    this._links.forEach(function (link) {
      const isActive = link.getAttribute('data-toc-link') === id;
      link.classList.toggle('is-active', isActive);
      if (isActive) activeLink = link;
    });

    if (!activeLink) return;
    const parentItem = activeLink.closest('.yg-article-toc__item--h2') || activeLink.closest('.yg-article-toc__sublist')?.parentElement;
    const parentToggle = parentItem?.querySelector(':scope > .yg-article-toc__row .yg-article-toc__toggle');
    this.querySelectorAll('.yg-article-toc__toggle:not([hidden])').forEach(function (toggle) {
      this._setExpanded(toggle, toggle === parentToggle);
    }.bind(this));
    this._keepLinkVisible(activeLink);
  }

  _keepLinkVisible(link) {
    if (!this._nav) return;
    const navRect = this._nav.getBoundingClientRect();
    const linkRect = link.getBoundingClientRect();
    if (linkRect.top < navRect.top) {
      this._nav.scrollTop -= navRect.top - linkRect.top;
    } else if (linkRect.bottom > navRect.bottom) {
      this._nav.scrollTop += linkRect.bottom - navRect.bottom;
    }
  }

  _readHash() {
    try {
      return decodeURIComponent(window.location.hash.slice(1));
    } catch (error) {
      return window.location.hash.slice(1);
    }
  }
}

if (!window.customElements.get('yg-article-toc')) {
  window.customElements.define('yg-article-toc', YGArticleToc);
}

class YGArticleRecommendedProducts extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    this._pauseReasons = new Set();
    this._index = 0;

    this._closeButton = this.querySelector('[data-recommended-products-close]');
    this._onClose = this._handleClose.bind(this);
    if (this._closeButton) this._closeButton.addEventListener('click', this._onClose);

    if (this.dataset.layout !== 'carousel') return;

    this._track = this.querySelector('[data-recommended-products-track]');
    this._viewport = this.querySelector('[data-recommended-products-viewport]');
    this._slides = Array.from(this.querySelectorAll('[data-recommended-products-slide]'));
    this._dots = Array.from(this.querySelectorAll('[data-recommended-products-dot]'));
    this._previousButton = this.querySelector('[data-recommended-products-previous]');
    this._nextButton = this.querySelector('[data-recommended-products-next]');

    if (!this._track || this._slides.length < 2) return;

    this._autoplayEnabled = this.dataset.autoplay === 'true';
    this._autoplayInterval = Math.max(3000, Number(this.dataset.autoplayInterval) || 5000);
    this._reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this._onPrevious = function () { this._goTo(this._index - 1, true); }.bind(this);
    this._onNext = function () { this._goTo(this._index + 1, true); }.bind(this);
    this._onPointerEnter = function () { this._setPaused('hover', true); }.bind(this);
    this._onPointerLeave = function () { this._setPaused('hover', false); }.bind(this);
    this._onFocusIn = function () { this._setPaused('focus', true); }.bind(this);
    this._onFocusOut = function (event) {
      if (!event.relatedTarget || !this.contains(event.relatedTarget)) this._setPaused('focus', false);
    }.bind(this);
    this._onVisibilityChange = function () { this._setPaused('visibility', document.hidden); }.bind(this);
    this._onReducedMotionChange = function () { this._syncAutoplay(); }.bind(this);
    this._onTouchStart = this._handleTouchStart.bind(this);
    this._onTouchEnd = this._handleTouchEnd.bind(this);
    this._onKeyDown = this._handleKeyDown.bind(this);
    this._onDotClick = function (event) {
      const dotIndex = Number(event.currentTarget.dataset.recommendedProductsDot);
      this._goTo(dotIndex, true);
    }.bind(this);

    if (this._previousButton) this._previousButton.addEventListener('click', this._onPrevious);
    if (this._nextButton) this._nextButton.addEventListener('click', this._onNext);
    this._dots.forEach(function (dot) { dot.addEventListener('click', this._onDotClick); }.bind(this));
    this.addEventListener('mouseenter', this._onPointerEnter);
    this.addEventListener('mouseleave', this._onPointerLeave);
    this.addEventListener('focusin', this._onFocusIn);
    this.addEventListener('focusout', this._onFocusOut);
    this.addEventListener('keydown', this._onKeyDown);
    this._viewport.addEventListener('touchstart', this._onTouchStart, { passive: true });
    this._viewport.addEventListener('touchend', this._onTouchEnd, { passive: true });
    document.addEventListener('visibilitychange', this._onVisibilityChange);

    if (this._reducedMotionQuery.addEventListener) {
      this._reducedMotionQuery.addEventListener('change', this._onReducedMotionChange);
    } else {
      this._reducedMotionQuery.addListener(this._onReducedMotionChange);
    }

    if (document.hidden) this._pauseReasons.add('visibility');
    this._goTo(0, false);
    this._syncAutoplay();
  }

  disconnectedCallback() {
    if (this._closeButton && this._onClose) this._closeButton.removeEventListener('click', this._onClose);
    if (this._previousButton && this._onPrevious) this._previousButton.removeEventListener('click', this._onPrevious);
    if (this._nextButton && this._onNext) this._nextButton.removeEventListener('click', this._onNext);
    if (this._dots && this._onDotClick) {
      this._dots.forEach(function (dot) { dot.removeEventListener('click', this._onDotClick); }.bind(this));
    }
    if (this._onPointerEnter) this.removeEventListener('mouseenter', this._onPointerEnter);
    if (this._onPointerLeave) this.removeEventListener('mouseleave', this._onPointerLeave);
    if (this._onFocusIn) this.removeEventListener('focusin', this._onFocusIn);
    if (this._onFocusOut) this.removeEventListener('focusout', this._onFocusOut);
    if (this._onKeyDown) this.removeEventListener('keydown', this._onKeyDown);
    if (this._viewport && this._onTouchStart) this._viewport.removeEventListener('touchstart', this._onTouchStart);
    if (this._viewport && this._onTouchEnd) this._viewport.removeEventListener('touchend', this._onTouchEnd);
    if (this._onVisibilityChange) document.removeEventListener('visibilitychange', this._onVisibilityChange);

    if (this._reducedMotionQuery && this._onReducedMotionChange) {
      if (this._reducedMotionQuery.removeEventListener) {
        this._reducedMotionQuery.removeEventListener('change', this._onReducedMotionChange);
      } else {
        this._reducedMotionQuery.removeListener(this._onReducedMotionChange);
      }
    }

    this._stopAutoplay();
    this._initialized = false;
  }

  _handleClose() {
    const sidebar = this.closest('.yg-news-article-sidebar');
    const focusTarget = sidebar?.querySelector('yg-article-toc [data-toc-link], yg-article-toc button:not([hidden])') || sidebar;

    this._stopAutoplay();
    this.hidden = true;
    if (focusTarget) focusTarget.focus({ preventScroll: true });
  }

  _handleTouchStart(event) {
    const touch = event.changedTouches[0];
    this._touchStartX = touch ? touch.clientX : null;
  }

  _handleTouchEnd(event) {
    if (this._touchStartX === null || this._touchStartX === undefined) return;
    const touch = event.changedTouches[0];
    if (!touch) return;

    const distance = touch.clientX - this._touchStartX;
    this._touchStartX = null;
    if (Math.abs(distance) < 40) return;
    this._goTo(this._index + (distance < 0 ? 1 : -1), true);
  }

  _handleKeyDown(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    this._goTo(this._index + (event.key === 'ArrowRight' ? 1 : -1), true);
  }

  _goTo(nextIndex, userInitiated) {
    if (!this._slides?.length) return;
    const slideCount = this._slides.length;
    this._index = (nextIndex + slideCount) % slideCount;
    this._track.style.transform = `translate3d(-${this._index * 100}%, 0, 0)`;

    this._slides.forEach(function (slide, index) {
      const isActive = index === this._index;
      slide.setAttribute('aria-hidden', String(!isActive));
      if (isActive) {
        slide.removeAttribute('inert');
      } else {
        slide.setAttribute('inert', '');
      }
    }.bind(this));

    this._dots.forEach(function (dot, index) {
      dot.setAttribute('aria-current', String(index === this._index));
    }.bind(this));

    if (userInitiated) {
      this._stopAutoplay();
      this._syncAutoplay();
    }
  }

  _setPaused(reason, paused) {
    if (paused) {
      this._pauseReasons.add(reason);
    } else {
      this._pauseReasons.delete(reason);
    }
    this._syncAutoplay();
  }

  _syncAutoplay() {
    this._stopAutoplay();
    if (!this._autoplayEnabled || this._reducedMotionQuery?.matches || this._pauseReasons.size > 0 || this.hidden) return;

    this._autoplayTimer = window.setInterval(function () {
      this._goTo(this._index + 1, false);
    }.bind(this), this._autoplayInterval);
  }

  _stopAutoplay() {
    if (!this._autoplayTimer) return;
    window.clearInterval(this._autoplayTimer);
    this._autoplayTimer = null;
  }
}

if (!window.customElements.get('yg-article-recommended-products')) {
  window.customElements.define('yg-article-recommended-products', YGArticleRecommendedProducts);
}

const initYGNewsArticles = function () {
  const articleIframes = document.querySelectorAll('.yg-news-article-content iframe[data-src]:not([src])');

  if (articleIframes.length > 0) {
    const loadArticleIframes = function () {
      articleIframes.forEach(function (iframe) {
        const src = iframe.getAttribute('data-src');

        if (src) {
          iframe.setAttribute('src', src);
          iframe.removeAttribute('data-src');
        }
      });
    };

    if (window.scrollY > 0) {
      loadArticleIframes();
    } else {
      window.addEventListener('scroll', loadArticleIframes, { once: true, passive: true });
    }
  }

  document.querySelectorAll('[data-copy-link]:not([data-copy-link-ready])').forEach(function (button) {
    button.setAttribute('data-copy-link-ready', 'true');
    button.addEventListener('click', async function () {
      const label = button.querySelector('[data-copy-link-label]');
      const status = button.parentElement.querySelector('[data-copy-link-status]');
      const defaultText = button.dataset.defaultText || '';
      let feedback = button.dataset.copiedText || defaultText;

      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(window.location.href);
        } else {
          const textArea = document.createElement('textarea');
          textArea.value = window.location.href;
          textArea.setAttribute('readonly', '');
          textArea.style.position = 'fixed';
          textArea.style.opacity = '0';
          document.body.appendChild(textArea);
          textArea.select();
          const copied = document.execCommand('copy');
          textArea.remove();
          if (!copied) throw new Error('Copy command failed');
        }
      } catch (error) {
        feedback = button.dataset.errorText || defaultText;
      }

      if (label) label.textContent = feedback;
      if (status) status.textContent = feedback;
      window.setTimeout(function () {
        if (label) label.textContent = defaultText;
        if (status) status.textContent = '';
      }, 1800);
    });
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initYGNewsArticles, { once: true });
} else {
  initYGNewsArticles();
}

document.addEventListener('shopify:section:load', initYGNewsArticles);
})();

